import { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  Paper,
  Typography,
  Chip,
  CircularProgress,
  alpha,
  useTheme,
  LinearProgress,
  Tooltip,
} from "@mui/material";
import {
  FlaskConical,
  Trophy,
  AlertTriangle,
  Info,
  MailOpen,
  MousePointerClick,
  Send,
} from "lucide-react";
import { fetchCampaignABResults } from "../../../../../../redux/actions/marketingAction";

/**
 * Two-proportion z-test for two independent samples.
 * Returns the absolute z-value. |z| >= 1.96 ~ 95% confidence.
 * Falls back to 0 when either sample is empty.
 */
const zScore = (xa, na, xb, nb) => {
  if (na === 0 || nb === 0) return 0;
  const pa = xa / na;
  const pb = xb / nb;
  const p = (xa + xb) / (na + nb);
  const denom = Math.sqrt(p * (1 - p) * (1 / na + 1 / nb));
  if (denom === 0) return 0;
  return Math.abs((pa - pb) / denom);
};

const significanceLabel = (z) => {
  if (z >= 2.58) return { text: "99%", color: "#10b981" };
  if (z >= 1.96) return { text: "95%", color: "#10b981" };
  if (z >= 1.64) return { text: "90%", color: "#f59e0b" };
  return { text: "Not significant", color: "#6b7280" };
};

const lift = (rateWinner, rateLoser) => {
  if (!rateLoser || rateLoser === 0) return null;
  return ((rateWinner - rateLoser) / rateLoser) * 100;
};

/* ──────────── Variant Card ──────────── */
const VariantCard = ({
  letter,
  subject,
  templateName,
  sent,
  opened,
  clicked,
  openRate,
  clickRate,
  isWinner,
  isLoser,
  primaryMetric,
}) => {
  const theme = useTheme();
  const winnerColor = "#10b981";
  const baseBorder = isWinner
    ? winnerColor
    : isLoser
    ? alpha(theme.palette.divider, 0.6)
    : theme.palette.divider;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: "12px",
        border: `${isWinner ? 2 : 1}px solid ${baseBorder}`,
        bgcolor: isWinner ? alpha(winnerColor, 0.04) : "transparent",
        flex: 1,
        minWidth: 260,
        position: "relative",
      }}
    >
      {isWinner && (
        <Box
          sx={{
            position: "absolute",
            top: -10,
            right: 12,
            bgcolor: winnerColor,
            color: "#fff",
            px: 1,
            py: 0.25,
            borderRadius: "6px",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            fontSize: "0.65rem",
            fontWeight: 700,
            textTransform: "uppercase",
          }}
        >
          <Trophy size={11} /> Winner
        </Box>
      )}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Chip
          label={letter.toUpperCase()}
          size="small"
          sx={{
            borderRadius: "6px",
            fontSize: "0.7rem",
            fontWeight: 700,
            bgcolor: isWinner ? alpha(winnerColor, 0.15) : alpha(theme.palette.primary.main, 0.1),
            color: isWinner ? winnerColor : theme.palette.primary.main,
            height: 22,
          }}
        />
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.85rem" }} noWrap>
          {subject || "(no subject)"}
        </Typography>
      </Box>
      {templateName && (
        <Typography
          variant="caption"
          sx={{ color: theme.palette.text.secondary, fontSize: "0.7rem", display: "block", mb: 1.25 }}
        >
          Body: {templateName}
        </Typography>
      )}

      <Box sx={{ display: "flex", gap: 2 }}>
        <Box>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: "0.65rem", textTransform: "uppercase" }}>
            Sent
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
            {sent.toLocaleString()}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: "0.65rem", textTransform: "uppercase" }}>
            Open rate
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              fontSize: "0.95rem",
              color: primaryMetric === "open_rate" && isWinner ? winnerColor : "inherit",
            }}
          >
            {openRate.toFixed(1)}%
            <Typography component="span" variant="caption" sx={{ ml: 0.5, color: theme.palette.text.secondary, fontWeight: 400 }}>
              ({opened})
            </Typography>
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: "0.65rem", textTransform: "uppercase" }}>
            Click rate
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              fontSize: "0.95rem",
              color: primaryMetric === "click_rate" && isWinner ? winnerColor : "inherit",
            }}
          >
            {clickRate.toFixed(1)}%
            <Typography component="span" variant="caption" sx={{ ml: 0.5, color: theme.palette.text.secondary, fontWeight: 400 }}>
              ({clicked})
            </Typography>
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

/* ──────────── Step Section ──────────── */
const StepSection = ({ result, idx }) => {
  const theme = useTheme();

  const aSent = result.variant_a_sent || 0;
  const bSent = result.variant_b_sent || 0;
  const aOpened = result.variant_a_opened || 0;
  const bOpened = result.variant_b_opened || 0;
  const aClicked = result.variant_a_clicked || 0;
  const bClicked = result.variant_b_clicked || 0;
  const aOpenRate = result.variant_a_open_rate || 0;
  const bOpenRate = result.variant_b_open_rate || 0;
  const aClickRate = result.variant_a_click_rate || 0;
  const bClickRate = result.variant_b_click_rate || 0;

  // Decide which metric drives the test:
  // body-level test (different B template) -> click rate; otherwise open rate
  const bodyDiffers = result.template_b_name && result.template_b_name !== result.template_a_name;
  const primaryMetric = bodyDiffers && (aClicked + bClicked) > 0 ? "click_rate" : "open_rate";

  // Significance test on the primary metric
  const z =
    primaryMetric === "click_rate"
      ? zScore(aClicked, aSent, bClicked, bSent)
      : zScore(aOpened, aSent, bOpened, bSent);
  const sig = significanceLabel(z);

  const winner = result.winner; // "a" | "b" | null
  const aWins = winner === "a";
  const bWins = winner === "b";

  // Lift between winner and loser on the primary metric
  let liftPct = null;
  if (winner) {
    if (primaryMetric === "click_rate") {
      liftPct = aWins ? lift(aClickRate, bClickRate) : lift(bClickRate, aClickRate);
    } else {
      liftPct = aWins ? lift(aOpenRate, bOpenRate) : lift(bOpenRate, aOpenRate);
    }
  }

  // Sample-size warning: cold email reply baselines are low; flag if either cell has < 100
  const lowSample = aSent < 100 || bSent < 100;

  // Progress bar shows sent volume vs. expected (for a quick visual)
  const totalSent = aSent + bSent;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: "12px",
        border: `1px solid ${theme.palette.divider}`,
        mb: 2,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        <Chip
          label={`Step ${idx + 1}`}
          size="small"
          sx={{
            borderRadius: "6px",
            fontSize: "0.7rem",
            fontWeight: 700,
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.main,
            height: 22,
          }}
        />
        <Tooltip
          title={
            primaryMetric === "click_rate"
              ? "Body-level test detected. Winner is decided on click rate."
              : "Subject-level test. Winner is decided on open rate."
          }
        >
          <Chip
            icon={<FlaskConical size={11} />}
            label={primaryMetric === "click_rate" ? "Click-rate test" : "Open-rate test"}
            size="small"
            sx={{
              borderRadius: "6px",
              fontSize: "0.7rem",
              height: 22,
              bgcolor: alpha("#8b5cf6", 0.1),
              color: "#8b5cf6",
              "& .MuiChip-icon": { color: "#8b5cf6", ml: 0.5 },
            }}
          />
        </Tooltip>
        {!winner && totalSent > 0 && (
          <Chip
            label="Running"
            size="small"
            sx={{
              borderRadius: "6px",
              fontSize: "0.7rem",
              height: 22,
              bgcolor: alpha("#f59e0b", 0.1),
              color: "#f59e0b",
              fontWeight: 600,
            }}
          />
        )}
        {winner && (
          <Chip
            icon={<Trophy size={11} />}
            label={`Winner: ${winner.toUpperCase()}${liftPct !== null ? ` (+${liftPct.toFixed(1)}%)` : ""}`}
            size="small"
            sx={{
              borderRadius: "6px",
              fontSize: "0.7rem",
              height: 22,
              bgcolor: alpha("#10b981", 0.12),
              color: "#10b981",
              fontWeight: 700,
              "& .MuiChip-icon": { color: "#10b981", ml: 0.5 },
            }}
          />
        )}
        <Chip
          label={`Significance: ${sig.text}`}
          size="small"
          sx={{
            borderRadius: "6px",
            fontSize: "0.7rem",
            height: 22,
            bgcolor: alpha(sig.color, 0.1),
            color: sig.color,
            fontWeight: 600,
          }}
        />
        {lowSample && (
          <Tooltip title="Either variant has fewer than 100 sends. Reads are directional, not statistically reliable.">
            <Chip
              icon={<AlertTriangle size={11} />}
              label="Low sample"
              size="small"
              sx={{
                borderRadius: "6px",
                fontSize: "0.7rem",
                height: 22,
                bgcolor: alpha("#f59e0b", 0.1),
                color: "#f59e0b",
                "& .MuiChip-icon": { color: "#f59e0b", ml: 0.5 },
              }}
            />
          </Tooltip>
        )}
      </Box>

      {/* Volume bar A vs B */}
      <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 1.5, mb: 2 }}>
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: "0.7rem", minWidth: 60 }}>
          Volume
        </Typography>
        <Box sx={{ flex: 1, display: "flex", height: 6, borderRadius: 3, overflow: "hidden", bgcolor: alpha(theme.palette.divider, 0.3) }}>
          <Box sx={{ width: `${totalSent > 0 ? (aSent / totalSent) * 100 : 50}%`, bgcolor: theme.palette.primary.main }} />
          <Box sx={{ width: `${totalSent > 0 ? (bSent / totalSent) * 100 : 50}%`, bgcolor: "#8b5cf6" }} />
        </Box>
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: "0.7rem", minWidth: 80, textAlign: "right" }}>
          {aSent} / {bSent}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <VariantCard
          letter="a"
          subject={result.subject_a}
          templateName={result.template_a_name}
          sent={aSent}
          opened={aOpened}
          clicked={aClicked}
          openRate={aOpenRate}
          clickRate={aClickRate}
          isWinner={aWins}
          isLoser={bWins}
          primaryMetric={primaryMetric}
        />
        <VariantCard
          letter="b"
          subject={result.subject_b}
          templateName={result.template_b_name}
          sent={bSent}
          opened={bOpened}
          clicked={bClicked}
          openRate={bOpenRate}
          clickRate={bClickRate}
          isWinner={bWins}
          isLoser={aWins}
          primaryMetric={primaryMetric}
        />
      </Box>
    </Paper>
  );
};

/* ════════════════════════════════════════════════════════════
   MAIN PANEL
   ════════════════════════════════════════════════════════════ */
const ABTestPanel = ({ campaignId }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await dispatch(fetchCampaignABResults(campaignId));
      // Endpoint returns either an array or { results: [...] }
      const list = Array.isArray(data) ? data : data?.results || [];
      setResults(list);
    } catch (e) {
      setError(e?.message || "Failed to load A/B test results");
    } finally {
      setLoading(false);
    }
  }, [dispatch, campaignId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) {
    return (
      <Paper elevation={0} sx={{ p: 3, borderRadius: "12px", border: `1px solid ${theme.palette.divider}`, textAlign: "center" }}>
        <AlertTriangle size={20} color="#ef4444" />
        <Typography variant="body2" sx={{ color: "#ef4444", mt: 1 }}>
          {error}
        </Typography>
      </Paper>
    );
  }

  if (!results || results.length === 0) {
    return (
      <Paper elevation={0} sx={{ p: 4, borderRadius: "12px", border: `1px solid ${theme.palette.divider}`, textAlign: "center" }}>
        <FlaskConical size={28} color={theme.palette.text.secondary} />
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 1.5 }}>
          No A/B tests configured on this campaign.
        </Typography>
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block", mt: 0.5 }}>
          Enable A/B testing on a sequence step to see results here.
        </Typography>
      </Paper>
    );
  }

  // Aggregate header stats
  const totalSent = results.reduce((s, r) => s + (r.variant_a_sent || 0) + (r.variant_b_sent || 0), 0);
  const totalOpened = results.reduce((s, r) => s + (r.variant_a_opened || 0) + (r.variant_b_opened || 0), 0);
  const totalClicked = results.reduce((s, r) => s + (r.variant_a_clicked || 0) + (r.variant_b_clicked || 0), 0);
  const winnerCount = results.filter((r) => r.winner).length;

  return (
    <Box>
      {/* Summary header */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: "12px",
          border: `1px solid ${theme.palette.divider}`,
          mb: 2.5,
          display: "flex",
          alignItems: "center",
          gap: 3,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <FlaskConical size={16} color={theme.palette.primary.main} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {results.length} A/B {results.length === 1 ? "test" : "tests"}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Send size={13} color={theme.palette.text.secondary} />
          <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
            {totalSent.toLocaleString()} sent across variants
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <MailOpen size={13} color={theme.palette.text.secondary} />
          <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
            {totalOpened.toLocaleString()} opens
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <MousePointerClick size={13} color={theme.palette.text.secondary} />
          <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
            {totalClicked.toLocaleString()} clicks
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Trophy size={13} color="#10b981" />
          <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
            {winnerCount} / {results.length} resolved
          </Typography>
        </Box>
      </Paper>

      {/* Methodology hint */}
      <Box
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "flex-start",
          mb: 2,
          p: 1.5,
          borderRadius: "8px",
          bgcolor: alpha(theme.palette.info.main || "#3b82f6", 0.06),
        }}
      >
        <Info size={14} color={theme.palette.info.main || "#3b82f6"} style={{ marginTop: 2, flexShrink: 0 }} />
        <Typography variant="caption" sx={{ fontSize: "0.72rem", lineHeight: 1.5, color: theme.palette.text.secondary }}>
          When the test varies the email body, the winner is decided on click rate. Subject-only tests use open rate. Significance is computed via two-proportion z-test (95% threshold = 1.96).
        </Typography>
      </Box>

      {/* Per-step results */}
      {results.map((r, idx) => (
        <StepSection key={r.step_id} result={r} idx={idx} />
      ))}
    </Box>
  );
};

export default ABTestPanel;
